import {
  Component,
  OnInit,
  Input,
  ViewEncapsulation,
  NgZone,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { OnboardingProductsStore } from './store/store';
import { UiActions, ValidateSessionActions } from './store/actions';
import { LoadingService } from './services/loading.service';
import { SessionService } from './services/session.service';
import { Observable, Subscription } from 'rxjs';
import {
  ParentCallerService,
  ParentRequest,
  ParentResponse,
} from './services/parent-caller.service';
import { DataClientService } from './services/data-client.service';
import { ContextService } from './services/context.service';
import { SetInitialSessionService } from './services/setInitialSessionService.service';
import { SessionMobile, WCSession } from './shared/interfaces/sessions';
import { ContentfulService } from './services/contentful.service';
import {
  PerformanceData,
  StaticAssets,
  StaticAssetsService,
} from './services/static-assets';
import { DatadogService } from './services/datadog/datadog.service';
import { StepsClientTypes } from './shared/enums';
import { RateInfo } from './shared/interfaces/contentfulInfo.interface';
import { AccountsNames } from './shared/interfaces/accountNames.interface';
import { environment } from '../environments/environment';
import { AccountFormatService } from './services/account-format.service';
import { DataNotClientService } from './services/data-not-client.service';

@Component({
  selector: 'wc-onboarding',
  templateUrl: './app.component.html',
  styleUrls: ['global.scss', './app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {
  // For Web Component
  @Input() set parentResponse(response: ParentResponse) {
    this.parentCallerService.sendResponse(response);
  }

  subs = new Subscription();
  performanceData: PerformanceData;
  rateInfo: RateInfo;

  staticAssetsUrl = environment.staticScripts.staticAssets;

  // Properties
  private outputSubscription: Subscription;
  private contentfulSubscription: Subscription;
  private isWc: boolean;
  public mode: string;
  public finishedRender = false;
  @Output() public parentEmitter =
  new EventEmitter<ParentRequest>();

  // Init
  constructor(
    private store: Store<OnboardingProductsStore.AppState>,
    public loadingService: LoadingService,
    private sessionService: SessionService,
    private zone: NgZone,
    private parentCallerService: ParentCallerService,
    private dataClientService: DataClientService,
    private dataNotClientService: DataNotClientService,
    private contextService: ContextService,
    private setInitialSessionService: SetInitialSessionService,
    private contentfulService: ContentfulService,
    private staticAssetsService: StaticAssetsService,
    private dataDogService: DatadogService,
    private accountFormatService: AccountFormatService
  ) {
    if (this.isWc) {
      this.callParentSubscription();
    }
    // this.callContentFullSubscription();
    // this.contentfulService.getContentfulEntries();
  }
  // For Mobile
  private validateSessionGetSuscription$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.validateSession)
  );

  validateSessionSubscription = this.validateSessionGetSuscription$.subscribe(
    ({ response, error }) => {
      if (error != null) {
        this.store.dispatch(ValidateSessionActions.cleanState());
        this.dataClientService.setValidationError(true);
        this.store.dispatch(
          UiActions.Navigate({ step: StepsClientTypes.STEP_SERVICE_ERROR })
        );
        this.loadingService.loading = false;
        this.finishedRender = true;
      }
      if (response !== null) {
        this.dataClientService.setValidationError(false);
        const accessToken = this.contextService.getCookie('accessToken');
        const sessionInfo: SessionMobile = {
          customerIdentificationNumber: response.identificationNumber,
          customerIdentificationType: response.identificationType,
          accessToken,
          isJointAccount: '0',
          mobilePlatform: response.platform
        };
        this.setInitialSessionService.setSessionMobile(
          sessionInfo,
          this.contextService.getCookie('accountType')
        );
        if (response.sessionId) {
          sessionStorage.setItem('sessionId', response.sessionId);
        }
        this.finishedRender = true;
      }
    }
  );
  /**
   * @param sessionInfo session info that gets sent from parent component in private site
   * @param _accountType not used, but might break wc call on private site
   */
  @Input() public setSession = (
    sessionInfo: WCSession,
    _accountType: string
  ) => {
    this.zone.run(() => {
      this.initWeb(sessionInfo);
    });
  }
  /**
   * Used for communicating information to parent, currently it is being used for notifying private site parents component:
   * - they must call healthcheck in onboarding
   * - they must refresh page after finished
   */
  callParentSubscription() {
    this.outputSubscription =
      this.parentCallerService.callParentRequest.subscribe(
        (request: ParentRequest) => {
          this.parentEmitter.next(request);
        }
      );
  }

  callContentFullSubscription() {
    this.loadingService.loading = true;
    this.contentfulSubscription = this.contentfulService
      .getEntriesFunction()
      .subscribe((entryCollection) => {
        if (entryCollection) {
          this.rateInfo = entryCollection.items[0].fields;
          // URL comes chopped so the http part has to be added manually
          this.rateInfo.backgroundImage.fields.file.url = `http:${this.rateInfo.backgroundImage.fields.file.url}`;
          this.contentfulService.setRateInfo(this.rateInfo);
          this.finishedRender = true;
          this.loadingService.loading = false;
        }
      });
  }

  ngOnInit() {
    this.loadingService.loading = true;
    this.sessionService.clearSession();
    this.mode = this.contextService.getMode();
    if (!this.contextService.isWC()) {
      this.contextService.isMobile() ? this.initMobile() : this.initWeb();
      this.loadingService.loading = false;
    }

    this.subs.add(
      this.getPerformanceData().subscribe((data) => {
        this.performanceData = data;
        this.dataNotClientService.setShowQR(this.performanceData.showQR);
        this.dataNotClientService.setIsRetakeActive(this.performanceData.isRetakeActive);
        this.dataNotClientService.setUseFlagr(this.performanceData.useFlagr);
        if ((this.performanceData.dataDog.dataDogState && environment.production) ||
          (this.performanceData.dataDog.preProdState && !environment.production)) {
          this.dataDogService.loadScript(this.performanceData.dataDog);
        }
      })
    );
    this.getAccountNames().subscribe((data) => {
      this.accountFormatService.setAccountNames(JSON.stringify(data));
    });
  }

  /**
   * This method is called in two ways
   * - Directly from ngOnInit if app is in public mode
   * - From the setSession input so that private site can start the web component on demand
   * @param sessionInfo Session info that was sent from private site
   */
  private initWeb(sessionInfo?: WCSession) {
    this.setInitialSessionService.setSession(sessionInfo);
    this.callContentFullSubscription();
  }

  private initMobile() {
    // Se setea session id en 1 para que el flujo no se rompa al agregar un header en null
    sessionStorage.setItem('sessionId', '1');
    const fingerprint = this.contextService.getCookie('fingerprint');
    const request = {
      fingerprint
    };
    this.store.dispatch(ValidateSessionActions.getValidateSession({ request }));
  }

  getPerformanceData(): Observable<PerformanceData> {
    return this.staticAssetsService.getStaticResources<PerformanceData>(
      StaticAssets.performanceData
    );
  }

  getAccountNames(): Observable<AccountsNames> {
    return this.staticAssetsService.getStaticResources<AccountsNames>(StaticAssets.accountNames);
  }

  ngOnDestroy() {
    if (this.outputSubscription) {
      this.outputSubscription.unsubscribe();
    }
    if (this.validateSessionSubscription) {
      this.validateSessionSubscription.unsubscribe();
    }
    if (this.subs) {
      this.subs.unsubscribe();
    }
  }
}
